import React, { useState, useRef } from "react";
import { TrendingDown, X, List, ScanLine } from "lucide-react";
import TransferCard, { TransferItem } from "./TransferCard";
import { toast } from "react-hot-toast";
import { SerialData } from "../../../store/slices/StockTransfer/stockTransferSlice";
import { isValidateSerialNumber } from "../../../utils/warehouse.util";
import PaginationControls from "../../../utils/PaginationControls";

interface OutgoingTransferSectionProps {
  outgoingData: {
    items: any[];
    total: number;
    loading: boolean;
    page: number;
    totalPages: number;
  };
  allSerials: SerialData[];
  onCancel: (
    items: Array<{ transferId: string; remark?: string }>,
  ) => Promise<boolean>;
  onPageChange: (page: number) => void;
  loadingAction: boolean;
  LoadingSpinner: () => JSX.Element;
}

const OutgoingTransferSection: React.FC<OutgoingTransferSectionProps> = ({
  outgoingData,
  allSerials,
  onCancel,
  onPageChange,
  loadingAction,
  LoadingSpinner,
}) => {
  const [activeTab, setActiveTab] = useState<"list" | "scan">("list");
  const [scanQuery, setScanQuery] = useState("");
  const [scannedTransfers, setScannedTransfers] = useState<SerialData[]>([]);
  const [remarks, setRemarks] = useState<Record<string, string>>({});
  const scanInputRef = useRef<HTMLInputElement>(null);

  const handleSingleCancel = async (id: string, remark: string) => {
    return await onCancel([{ transferId: id, remark }]);
  };

  const handleScan = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      const serial = scanQuery.trim();
      if (!serial) return;

      if (!isValidateSerialNumber(serial)) {
        return;
      }

      if (
        scannedTransfers.find(
          (t) => t.serialNumber.toLowerCase() === serial.toLowerCase(),
        )
      ) {
        setScanQuery("");
        return;
      }

      // Check against Master List
      const found = allSerials.find(
        (t) => t.serialNumber.toLowerCase() === serial.toLowerCase(),
      );

      if (found) {
        setScannedTransfers((prev) => [...prev, found]);
        setScanQuery("");
      } else {
        toast.error("Not found");
      }
    }
  };

  const handleBulkCancel = async () => {
    const payload = scannedTransfers.map((item) => ({
      transferId: item._id,
      remark: remarks[item._id] || "",
    }));
    const success = await onCancel(payload);
    if (success) {
      setScannedTransfers([]);
      setRemarks({});
    }
  };

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* Header */}
      <div className="flex flex-col gap-4 border-b border-gray-100 p-4 sm:p-6 md:flex-row md:items-center md:justify-between">
        <h2 className="flex items-center gap-2 text-lg font-bold text-gray-800 sm:text-xl">
          <TrendingDown size={20} className="shrink-0 text-yellow-600" />
          Outgoing product transfers ({outgoingData.total})
        </h2>
        <div className="grid w-full grid-cols-2 rounded-lg bg-gray-100 p-1 md:w-auto">
          <button
            type="button"
            onClick={() => setActiveTab("list")}
            className={`flex min-h-11 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors sm:px-4 ${
              activeTab === "list"
                ? "bg-white text-gray-800 shadow-sm"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            <List size={16} /> List
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab("scan");
              setTimeout(() => scanInputRef.current?.focus(), 100);
            }}
            className={`flex min-h-11 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors sm:px-4 ${
              activeTab === "scan"
                ? "bg-white text-[#0077C0] shadow-sm"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            <ScanLine size={16} /> Scan
          </button>
        </div>
      </div>

      {activeTab === "list" ? (
        <>
          <div className="overflow-x-auto">
            {outgoingData.loading && outgoingData.items.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-gray-500">
                Loading outgoing transfers...
              </div>
            ) : outgoingData.items.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <p className="font-semibold text-gray-800">
                  No pending outgoing transfers
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  Transfers created from this warehouse will appear here.
                </p>
              </div>
            ) : (
              <table className="w-full divide-y divide-gray-200 text-left lg:min-w-[1150px]">
                <thead className="hidden bg-gray-50 text-xs font-semibold uppercase text-gray-500 lg:table-header-group">
                  <tr>
                    <th className="px-4 py-3">Serial number</th>
                    <th className="px-4 py-3">Product name</th>
                    <th className="px-4 py-3">Warehouse route</th>
                    <th className="px-4 py-3">Sent by / date</th>
                    <th className="px-4 py-3">Transfer costs</th>
                    <th className="px-4 py-3">Sender remark</th>
                    <th className="px-4 py-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {outgoingData.items.map((transfer) => (
                    <TransferCard
                      key={transfer._id}
                      transfer={transfer as TransferItem}
                      type="outgoing"
                      actionLoading={loadingAction}
                      onConfirmAction={handleSingleCancel}
                      LoadingSpinner={LoadingSpinner}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {outgoingData.items.length > 0 && (
            <div className="border-t border-gray-200">
              <PaginationControls
                currentPage={outgoingData.page}
                totalPages={outgoingData.totalPages || 1}
                onPageChange={onPageChange}
                isLoading={outgoingData.loading}
                totalItems={outgoingData.total}
              />
            </div>
          )}
        </>
      ) : (
        <div className="animate-fade-in p-4 sm:p-6">
          <div className="mb-5">
            <label className="mb-2 block text-sm font-semibold text-gray-800">
              Scan outgoing serials
            </label>
            <input
              inputMode="numeric"
              maxLength={10}
              ref={scanInputRef}
              value={scanQuery}
              onChange={(e) => setScanQuery(e.target.value)}
              onKeyDown={handleScan}
              placeholder="Scan or enter a 10-digit serial number"
              className="min-h-12 w-full rounded-lg border border-gray-300 px-4 py-3 text-base focus:border-[#0077C0] focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>
          {scannedTransfers.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-orange-200 bg-orange-50/50">
              <div className="flex items-center justify-between border-b border-orange-200 bg-orange-100/50 px-4 py-3">
                <h3 className="text-sm font-semibold text-orange-900">
                  Ready to cancel
                </h3>
                <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-orange-800">
                  {scannedTransfers.length}
                </span>
              </div>
              <div className="grid gap-3 p-3 sm:p-4">
                {scannedTransfers.map((item) => (
                  <article
                    key={item._id}
                    className="rounded-lg border border-orange-100 bg-white p-3 shadow-sm sm:p-4"
                  >
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <span className="font-mono font-semibold text-gray-900">
                        {item.serialNumber}
                      </span>
                      <button
                        type="button"
                        aria-label={`Remove ${item.serialNumber}`}
                        onClick={() =>
                          setScannedTransfers((previous) =>
                            previous.filter(
                              (transfer) => transfer._id !== item._id,
                            ),
                          )
                        }
                        className="flex min-h-10 min-w-10 items-center justify-center rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600"
                      >
                        <X size={18} />
                      </button>
                    </div>
                    <label className="block text-xs font-medium text-gray-600">
                      Cancellation reason
                      <input
                        className="mt-1 min-h-10 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#0077C0] focus:outline-none focus:ring-2 focus:ring-blue-100"
                        placeholder="Optional reason"
                        value={remarks[item._id] || ""}
                        onChange={(e) =>
                          setRemarks((previous) => ({
                            ...previous,
                            [item._id]: e.target.value,
                          }))
                        }
                      />
                    </label>
                  </article>
                ))}
              </div>
              <div className="flex justify-end border-t border-orange-200 p-3 sm:p-4">
                <button
                  type="button"
                  onClick={handleBulkCancel}
                  disabled={loadingAction}
                  className="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-red-700 px-5 py-2 text-sm font-semibold text-white transition hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {loadingAction && <LoadingSpinner />} Cancel Selected (
                  {scannedTransfers.length})
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default OutgoingTransferSection;
